import { useEffect } from "react";
import { Link } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { cx } from "@/lib/cx";
import { DESTINATIONS } from "@/lib/destinations";
import { useThemePreference } from "@/lib/use-theme-preference";

/**
 * Story 18.2 — the dark-mode toggle. A two-state, immediate-effect switch
 * (Owner decision, `epic-18-dark-mode.md`): no separate "Save" step, unlike
 * `NotificationToggleRow`'s admin-edits-someone-else click-to-edit-then-save
 * pattern (`UserRecord.tsx`) — that shape exists for an Owner editing
 * another person's settings, which this is not. The visible palette flips
 * in the same click (an optimistic write to the shared `useCurrentActor()`
 * cache, which `ThemeSync.tsx` reacts to), then the choice is persisted
 * through `PATCH /auth/me/theme-preference`; a failed request rolls the
 * cache back and shows the same "please try again" wording every other
 * screen's failed mutation uses.
 *
 * No dedicated `Switch` component exists yet (judgement call 1) — a plain
 * `role="switch"` button, styled inline, is the smaller diff than either
 * building one for this single call site or bending `Checkbox.tsx`'s own
 * checkbox semantics to look like something else.
 *
 * Story 20.1: the read/write mechanism itself now lives in
 * `useThemePreference()` (`lib/use-theme-preference.ts`), shared with the
 * new nav-bar theme toggle — this component only owns the switch markup.
 */
function DarkModeToggle() {
  const { actor, isDark, toggle, error } = useThemePreference();

  if (!actor) {
    return null;
  }

  return (
    <div className="mt-6">
      <label className="inline-flex min-h-tap-target-min cursor-pointer items-center gap-3 text-body text-ink">
        <button
          type="button"
          role="switch"
          aria-checked={isDark}
          onClick={() => void toggle()}
          className={cx(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
            isDark ? "bg-accent" : "bg-border-strong",
          )}
        >
          <span
            aria-hidden="true"
            className={cx(
              "inline-block h-5 w-5 translate-x-0.5 rounded-full bg-canvas transition-transform",
              isDark && "translate-x-5",
            )}
          />
        </button>
        Dark mode
      </label>
      {error ? (
        <p role="alert" className="mt-2 text-body text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Settings stays a placeholder for everything except Users, Alerts, Backup,
 * Reference lists, Import, Import batches and Municipalities (Epic 2's own
 * judgement call 5, "Epic 2 doesn't own building all of Settings, only this
 * one list" — extended by Story 3.8, which added the second real child,
 * Story 4.5, which added the third, and Story 5.2, which adds the fourth,
 * Story 8.2, which adds the fifth, Story 8.4, which adds the sixth, and
 * Story 15.1, which adds the seventh) — real links to `/settings/users`,
 * `/settings/alerts`, `/settings/backup`, `/settings/reference-lists`,
 * `/settings/import`, `/settings/import-batches` and
 * `/settings/municipalities`; every other eventual child (notification
 * preferences, ...) still shows only its placeholder sentence.
 *
 * Story 18.2 adds the page's first real self-service control, `DarkModeToggle`
 * — rendered directly here, not a new sub-route (judgement call 2): one
 * toggle doesn't warrant its own page the way the seven admin screens above
 * do.
 */
export function Settings() {
  const destination = DESTINATIONS.find((d) => d.id === "settings");
  if (!destination) {
    throw new Error('No destination is registered with id "settings".');
  }

  useEffect(() => {
    document.title = `${destination.label} — PropDesk`;
  }, [destination.label]);

  return (
    <div className="max-w-record-column-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">{destination.label}</h1>
      <p className="mt-4 text-body text-ink-secondary">{destination.sentence}</p>
      <DarkModeToggle />
      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/settings/users"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Users
        </Link>
        <Link
          to="/settings/alerts"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Alerts
        </Link>
        <Link
          to="/settings/backup"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Backup
        </Link>
        <Link
          to="/settings/reference-lists"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Reference lists
        </Link>
        <Link
          to="/settings/import"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Import
        </Link>
        <Link
          to="/settings/import-batches"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Import batches
        </Link>
        <Link
          to="/settings/municipalities"
          className="inline-flex h-tap-target-min items-center rounded-md border border-border-strong bg-canvas px-5 text-button text-ink hover:bg-surface-hover"
        >
          Municipalities
        </Link>
      </div>
    </div>
  );
}
